import { CheckIcon, Scrollbar, Spinner, type ListViewStatus } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { RefreshIcon } from "@/components/icons";
import { EmptyState } from "@/shared/ui/emptyState/public";
import styles from "./MultiSelectList.module.css";

// 다중 선택 listbox. c6 ListView 와 같은 위치에 쓰지만 **선택 모델이 다르다.**
//
// c6 ListView 는 단일 선택이고 selection-follows-focus 다(↑/↓ 가 선택을 옮긴다, RFC 012 §선택).
// WAI-ARIA 에 따르면 다중 선택 listbox 는 포커스와 선택을 분리해야 한다. ↑/↓ 와 행 클릭은
// 포커스(커서)만 옮긴다. 토글은 Space/Enter 와 체크박스 영역 클릭으로 한다. 커서는
// onActiveItemChange 로 소비처에 전달된다. 소비처는 미리보기처럼 선택과 별개인 기능을 연결할 수 있다.
// 한 컴포넌트에 두 모델을 넣으면 모든 키 분기에 모드 검사가 필요하다. c6 가 SearchInput 과
// TokenSearchInput 을 selection 모델에 따라 나눈 것과 같은 이유로 여기서 분리한다.
//
// 소비처가 하나(DatasetPicker)이므로 앱에 둔다. 두 번째 소비처가 생기면 c6 ListView family 로
// 옮긴다. RFC 012 §비목표가 `aria-multiselectable` 확장을 이미 예정해 두었다.

// 선택 모델. multi 는 체크박스로 여러 개를 담는다. single 은 체크박스 없이 행 클릭으로 하나를 고른다.
// 두 모드의 **키보드 계약은 같다**(↑/↓ 는 커서만 옮기고, Space/Enter 가 토글한다).
// single 을 c6 ListView 처럼 selection-follows-focus 로 두면 이동만 해도 선택이 바뀐다. 그러면
// 미리보기 커서(onActiveItemChange)와 담기를 나눈 의미가 없다.
// "다시 누르면 해제인가"는 호출부가 정한다. single 소비처는 선택을 교체한다.
export type MultiSelectListMode = "single" | "multi";

export type MultiSelectListItemContext = {
  selected: boolean;
  index: number;
};

export type MultiSelectListProps<T> = {
  items: readonly T[];
  getKey: (item: T) => string;
  selectedKeys: ReadonlySet<string>;
  onToggle: (item: T, index: number) => void;
  selectionMode?: MultiSelectListMode;
  renderItem: (item: T, context: MultiSelectListItemContext) => ReactNode;
  // 커서(포커스)가 이동할 때마다 알린다. 클릭과 키보드 이동 모두 해당한다. 선택과는 관계없다.
  onActiveItemChange?: (item: T, index: number) => void;

  // first-page 로드 상태. items 는 success 일 때만 채워 전달한다(c6 ListView 와 같은 계약).
  status?: ListViewStatus;
  errorMessage?: string | null;
  emptyMessage?: ReactNode;
  // 첫 페이지 조회 실패에서 다시 받는다. 생략하면 실패 안내에 버튼을 두지 않는다.
  onRetry?: () => void;

  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;

  ariaLabel?: string;
  className?: string;
};

export function MultiSelectList<T>({
  items,
  getKey,
  selectedKeys,
  onToggle,
  selectionMode = "multi",
  renderItem,
  onActiveItemChange,
  status = "success",
  errorMessage,
  emptyMessage = "표시할 항목이 없습니다.",
  onRetry,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  ariaLabel = "목록",
  className,
}: MultiSelectListProps<T>): ReactNode {
  const itemRefs = useRef(new Map<string, HTMLDivElement>());
  const scrollerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  // 포커스 커서. 선택과 분리된 축이다. 다중 선택 listbox 에서 ↑/↓ 는 커서만 옮긴다.
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const activeExists = items.some((item) => getKey(item) === activeKey);
  // roving tabindex. 커서(없으면 첫 항목)만 Tab 정지점이므로 listbox 전체가 한 정지점이 된다.
  const tabStopKey = activeExists ? activeKey : items.length > 0 ? getKey(items[0]) : null;

  function registerItem(key: string, element: HTMLDivElement | null): void {
    if (element) {
      itemRefs.current.set(key, element);
      return;
    }
    itemRefs.current.delete(key);
  }

  // 목록 맨 아래 sentinel 이 스크롤 컨테이너에 들어오면 다음 페이지를 요청한다. sentinel 을 비율
  // 인덱스가 아니라 바닥에 고정한다. 그래서 빠르게 스크롤해 지나가도 항상 다시 관측된다
  // (c6 ListView 와 같은 방식).
  useEffect(() => {
    if (!hasMore || !onLoadMore) {
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      return;
    }
    const trigger = triggerRef.current;
    const root = scrollerRef.current;
    if (!trigger || !root) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore();
        }
      },
      { root, rootMargin: "0px 0px 20% 0px" },
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, items]);

  // 포커스만 옮긴다. 선택은 Space/Enter 와 클릭이 소유한다.
  function moveFocus(index: number): void {
    const next = items[index];
    if (next === undefined) {
      return;
    }
    const key = getKey(next);
    setActiveKey(key);
    itemRefs.current.get(key)?.focus({ preventScroll: true });
    const element = itemRefs.current.get(key);
    if (element) {
      scrollItemIntoView(element, scrollerRef.current);
    }
  }

  function toggleAt(index: number): void {
    const item = items[index];
    if (item === undefined) {
      return;
    }
    onToggle(item, index);
  }

  function handleOptionKeyDown(event: KeyboardEvent<HTMLDivElement>, index: number): void {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveFocus(Math.min(items.length - 1, index + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        moveFocus(Math.max(0, index - 1));
        break;
      case "Home":
        event.preventDefault();
        moveFocus(0);
        break;
      case "End":
        event.preventDefault();
        moveFocus(items.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        toggleAt(index);
        break;
      default:
        break;
    }
  }

  return (
    <Scrollbar ref={scrollerRef} className={className}>
      {status === "loading" || status === "idle" ? (
        // Spinner 는 label 이 없는 장식(aria-hidden)이다. 그래서 로딩 상태를 알리는 표시를 컨테이너에 둔다.
        <div className={styles.loader} data-loading="true">
          <Spinner size="md" />
        </div>
      ) : null}
      {status === "error" ? (
        <EmptyState
          tone="error"
          action={
            onRetry === undefined
              ? undefined
              : { label: "다시 시도", onClick: onRetry, icon: <RefreshIcon /> }
          }
        >
          {errorMessage}
        </EmptyState>
      ) : null}
      {status === "success" && items.length === 0 ? <EmptyState>{emptyMessage}</EmptyState> : null}
      {status === "success" && items.length > 0 ? (
        <div
          className={styles.listbox}
          role="listbox"
          aria-multiselectable={selectionMode === "multi" ? true : undefined}
          aria-label={ariaLabel}
        >
          {items.map((item, index) => {
            const key = getKey(item);
            const selected = selectedKeys.has(key);
            return (
              <div
                key={key}
                ref={(element) => registerItem(key, element)}
                role="option"
                aria-selected={selected}
                tabIndex={key === tabStopKey ? 0 : -1}
                className={styles.item}
                data-selected={selected ? "true" : undefined}
                data-active={key === activeKey ? "true" : undefined}
                // multi 에서 클릭은 별도 핸들러 없이 포커스로 커서를 옮긴다. 토글은 Space/Enter 와
                // 체크박스 영역이 소유한다(파일 탐색기 패턴: 확인과 담기의 분리).
                // single 은 담을 수 있는 항목이 하나뿐이므로 그 분리가 의미가 없다. 고른 항목이
                // 곧 미리보기 대상이다. 그래서 행 전체가 토글의 포인터 표적이다.
                onClick={selectionMode === "single" ? () => toggleAt(index) : undefined}
                onFocus={() => {
                  setActiveKey(key);
                  onActiveItemChange?.(item, index);
                }}
                onKeyDown={(event) => handleOptionKeyDown(event, index)}
              >
                {/* 체크박스 영역. 포인터로 토글하는 경로다(행 클릭은 커서만 옮긴다).
                    AT 에는 옵션의 aria-selected 가 이미 상태를 알리고, 키보드는 Space/Enter 로
                    토글한다. 그래서 중첩 인터랙티브 요소(실제 checkbox)로 두지 않고 장식과
                    클릭 영역으로만 둔다. */}
                {selectionMode === "multi" ? (
                  <span
                    className={styles.indicator}
                    aria-hidden="true"
                    data-part="toggle"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleAt(index);
                    }}
                  >
                    {selected ? <CheckIcon /> : null}
                  </span>
                ) : null}
                <span className={styles.itemBody}>{renderItem(item, { selected, index })}</span>
              </div>
            );
          })}
        </div>
      ) : null}
      {status === "success" && hasMore && onLoadMore ? (
        <div ref={triggerRef} className={styles.loadTrigger} aria-hidden="true" />
      ) : null}
      {status === "success" && loadingMore ? (
        <div className={styles.sentinel} aria-hidden="true">
          <Spinner size="sm" />
        </div>
      ) : null}
    </Scrollbar>
  );
}

// 커서 항목이 보이도록 스크롤 컨테이너를 스크롤한다. offsetTop 은 offsetParent 기준이므로
// 컨테이너가 positioned 가 아니면 좌표계가 달라진다. 그래서 뷰포트 rect 차이로 계산한다
// (c6 ListView 와 같은 이유).
function scrollItemIntoView(item: HTMLElement, container: HTMLElement | null): void {
  if (!container) {
    return;
  }
  const itemRect = item.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  if (itemRect.top < containerRect.top) {
    container.scrollTop -= containerRect.top - itemRect.top;
  } else if (itemRect.bottom > containerRect.bottom) {
    container.scrollTop += itemRect.bottom - containerRect.bottom;
  }
}
